import { graphql } from "@/graphql/generated/gql";

export const inventoryDetailFieldsFragment = graphql(/* GraphQL */ `
  fragment InventoryDetailFields on Inventory {
    ...InventoryFields
    sectorList {
      ...InventorySectorTreeFields
    }
    methodology {
      ...MethodologyFields
      sectorList {
        ...SectorFields
        sectorSliceList {
          id
          methodologyId
          sectorId
          sectorSliceGroup
          title
          abbreviation
          description
          createdAt
          updatedAt
        }
        subsectorList {
          id
          methodologyId
          sectorId
          kind
          title
          abbreviation
          description
          createdAt
          updatedAt
          subsubsectorList {
            id
            methodologyId
            subsectorId
            kind
            title
            abbreviation
            description
            createdAt
            updatedAt
          }
        }
        energySourceInstanceList {
          ...EnergySourceInstanceFields
          energySource {
            ...EnergySourceFields
          }
        }
      }
    }
    inventoryValueList {
      ...InventoryValueFields
    }
    childInventoryList {
      id
      title
      description
      methodologyId
      year
      isMultiregion
      informational
      parentInventoryId
      rootInventoryId
    }
    localInstallationList {
      id
      inventoryId
      workspaceId
      name
      type
      inputs
      outputs
      createdAt
      updatedAt
    }
    createdAt
    updatedAt
  }
`);
export const getInventoryQueryDocument = graphql(/* GraphQL */ `
  query GetInventory($input: GetInventoryInput!) {
    inventory(getInventoryInput: $input) {
      ...InventoryDetailFields
    }
  }
`);

export const getInventoryTransportPrefillStatusQueryDocument = graphql(/* GraphQL */ `
  query GetInventoryTransportPrefillStatus($input: GetInventoryInput!) {
    inventory(getInventoryInput: $input) {
      id
      transportPrefillStatus
    }
  }
`);

export const getInventoryListQueryDocument = graphql(/* GraphQL */ `
  query GetInventoryList($input: GetInventoryListInput!) {
    inventoryList(getInventoryListInput: $input) {
      ...InventoryDetailFields
    }
  }
`);

export const inventorySectorTreeFieldsFragment = graphql(/* GraphQL */ `
  fragment InventorySectorTreeFields on Sector {
    ...SectorFields
    sectorSliceList {
      ...SectorSliceFields
    }
    subsectorList {
      ...SubsectorFields
      subsubsectorList {
        ...SubsubsectorFields
      }
    }
    energySourceInstanceList {
      ...EnergySourceInstanceFields
      energySource {
        ...EnergySourceFields
      }
    }
  }
`);

export const inventoryComparisonFieldsFragment = graphql(/* GraphQL */ `
  fragment InventoryComparisonFields on Inventory {
    ...PublicInventoryBaseFields
    sectorList {
      ...InventorySectorTreeFields
    }
    methodology {
      id
      title
      description
      createdAt
      updatedAt
      sectorList {
        ...InventorySectorTreeFields
      }
    }
    inventoryValueList {
      ...InventoryValueFields
    }
  }
`);

export const getInventoriesByIdsQueryDocument = graphql(/* GraphQL */ `
  query GetInventoriesByIds($input: GetInventoriesByIdsInput!) {
    inventoriesByIds(getInventoriesByIdsInput: $input) {
      ...InventoryComparisonFields
    }
  }
`);

export const getInventoryListForKspImportQueryDocument = graphql(/* GraphQL */ `
  query GetInventoryListForKspImport($input: GetInventoryListInput!) {
    inventoryList(getInventoryListInput: $input) {
      id
      title
      year
      methodologyId
      informational
      # The inventory's own tree (post-materialize for informational
      # inventories). KSP merges must land on inventory-scoped subsector ids,
      # not methodology ones — otherwise the values point at the wrong rows
      # and recompute trust-mode silently drops them.
      sectorList {
        id
        title
        kind
        subsectorList {
          id
          sectorId
          title
          kind
        }
      }
      inventoryValueList {
        id
        sectorId
        energySourceId
        subsectorId
        amount
        type
      }
    }
  }
`);

export const getInventoryListMinimalQueryDocument = graphql(/* GraphQL */ `
  query GetInventoryListMinimal($input: GetInventoryListInput!) {
    inventoryList(getInventoryListInput: $input) {
      id
      methodologyId
      title
      year
      isMultiregion
      informational
      totalEnergyConsumption
      totalEmissions
      createdAt
    }
  }
`);

export const getInventoryListTableQueryDocument = graphql(/* GraphQL */ `
  query GetInventoryListTable($input: GetInventoryListInput!) {
    inventoryList(getInventoryListInput: $input) {
      id
      methodologyId
      title
      description
      year
      informational
      totalEnergyConsumption
      totalEmissions
      totalDataQuality
      createdAt
      updatedAt
      isBiskoCompliant
      methodology {
        title
      }
      inventoryReportingTotals {
        energyConsumption
        emissions
        dataQuality
      }
    }
  }
`);

// ============== MUTATIONS ======================
export const createInventoryMutationDocument = graphql(/* GraphQL */ `
  mutation CreateInventory($input: CreateInventoryInput!) {
    createInventory(createInventoryInput: $input) {
      ...InventoryFields
      # Inventory's own sector tree — post-materialize for informational
      # inventories. KSP uses this to resolve inventory-scoped subsector
      # ids for the upserts that follow create.
      sectorList {
        id
        title
        kind
        subsectorList {
          id
          sectorId
          title
          kind
        }
      }
      methodology {
        ...MethodologyFields
        sectorList {
          ...SectorFields
          sectorSliceList {
            id
            methodologyId
            sectorId
            sectorSliceGroup
            title
            abbreviation
            description
            createdAt
            updatedAt
          }
          subsectorList {
            id
            methodologyId
            sectorId
            kind
            title
            abbreviation
            description
            createdAt
            updatedAt
            subsubsectorList {
              id
              methodologyId
              subsectorId
              kind
              title
              abbreviation
              description
              createdAt
              updatedAt
            }
          }
          energySourceInstanceList {
            ...EnergySourceInstanceFields
            energySource {
              ...EnergySourceFields
            }
          }
        }
      }
      inventoryValueList {
        ...InventoryValueFields
      }
      childInventoryList {
        id
        title
        description
        methodologyId
        year
        isMultiregion
        parentInventoryId
        rootInventoryId
      }
      localInstallationList {
        id
        inventoryId
        workspaceId
        name
        type
        inputs
        outputs
        createdAt
        updatedAt
      }
      createdAt
      updatedAt
    }
  }
`);

export const updateInventoryMutationDocument = graphql(/* GraphQL */ `
  mutation UpdateInventory($input: UpdateInventoryInput!) {
    updateInventory(updateInventoryInput: $input) {
      ...InventoryFields
      methodology {
        ...MethodologyFields
        sectorList {
          ...SectorFields
          subsectorList {
            id
            methodologyId
            sectorId
            kind
            title
            abbreviation
            description
            createdAt
            updatedAt
            subsubsectorList {
              id
              methodologyId
              subsectorId
              kind
              title
              abbreviation
              description
              createdAt
              updatedAt
            }
          }
          sectorSliceList {
            id
            methodologyId
            sectorSliceGroup
            sectorId
            title
            abbreviation
            description
            createdAt
            updatedAt
          }
          energySourceInstanceList {
            ...EnergySourceInstanceFields
            energySource {
              ...EnergySourceFields
            }
          }
        }
      }
      inventoryValueList {
        ...InventoryValueFields
      }
      childInventoryList {
        id
        title
        description
        methodologyId
        year
        isMultiregion
        parentInventoryId
        rootInventoryId
      }
      localInstallationList {
        id
        inventoryId
        workspaceId
        name
        type
        inputs
        outputs
        createdAt
        updatedAt
      }
      createdAt
      updatedAt
    }
  }
`);

export const deleteInventoryMutationDocument = graphql(/* GraphQL */ `
  mutation DeleteInventory($input: DeleteInventoryInput!) {
    deleteInventory(deleteInventoryInput: $input) {
      ...InventoryFields
      childInventoryList {
        id
        title
        description
        methodologyId
        year
        isMultiregion
        parentInventoryId
        rootInventoryId
      }
      localInstallationList {
        id
        inventoryId
        workspaceId
        name
        type
        inputs
        outputs
        createdAt
        updatedAt
      }
      createdAt
      updatedAt
    }
  }
`);
